/**
 * Timeframe strings ↔ bar specs (SPEC §12.2A catalogue). One canonical, case-sensitive string per
 * spec is what layouts and the synced favourites store; `parseTimeframeInput` is the lenient
 * parser behind the picker's custom box and the chart's type-to-change shortcut.
 *
 * | Canonical | Bar type                                   | Limits                        |
 * |-----------|--------------------------------------------|-------------------------------|
 * | `Ns`      | N-second time bars (from the session open) | 1–3600                        |
 * | `Nm`      | N-minute time bars                         | 1–720                         |
 * | `Nh`      | N-hour time bars (17:00 CT aligned)        | 1–12                          |
 * | `ND`      | N CME trading days (17:00 CT → 16:00 CT)   | 1–30                          |
 * | `NW`      | N trading weeks (Sunday open → Fri close)  | 1–52                          |
 * | `Nmo`     | calendar months of trading days            | 1, 2, 3, 4, 6, 12             |
 * | `Nt`      | N trades                                   | 1–100 000                     |
 * | `Nv`      | N contracts                                | 1–1 000 000                   |
 * | `Nr`      | range bars, N ticks                        | 1–10 000                      |
 * | `Nrk`     | classic Renko, N-tick bricks               | 1–10 000                      |
 *
 * Minutes are lowercase `m`; months are `mo`. The old picker *displayed* "1M" for one minute (it
 * stored "1m"), so `migrateTimeframe` reads a stored uppercase `NM` as minutes, while the custom
 * input box follows the TradingView convention and reads `1M` as one month.
 */
import type { BarSpec } from "./barBuilder.ts";

const S = 1000;
const MIN = 60 * S;
const HR = 60 * MIN;

export const MAX_SECONDS = 3600;
export const MAX_MINUTES = 720;
export const MAX_HOURS = 12;
export const MAX_TICKS = 100_000;
export const MAX_VOLUME = 1_000_000;
export const MAX_RANGE_TICKS = 10_000;
/** N-day bars: up to 30 trading days (longer spans are months). */
export const MAX_SESSION_DAYS = 30;
/** N-week bars: up to 52 weeks (a year). */
export const MAX_SESSION_WEEKS = 52;
/** Month groupings must divide a year so buckets stay aligned to January. */
export const MONTH_COUNTS: readonly number[] = [1, 2, 3, 4, 6, 12];

/** Validate a spec (the builder and parsers reject anything else). Returns an error or null. */
export function specError(spec: BarSpec): string | null {
  const whole = (n: number, lo: number, hi: number): boolean => Number.isInteger(n) && n >= lo && n <= hi;
  switch (spec.kind) {
    case "time": {
      const p = spec.periodMs;
      if (!whole(p, S, MAX_MINUTES * MIN) || p % S !== 0) return "time bars: 1 s to 12 h, whole seconds";
      return null;
    }
    case "session":
      if (spec.unit === "month")
        return MONTH_COUNTS.includes(spec.count) ? null : "months: 1, 2, 3, 4, 6 or 12";
      if (spec.unit === "day")
        return whole(spec.count, 1, MAX_SESSION_DAYS)
          ? null
          : `daily bars: 1 to ${String(MAX_SESSION_DAYS)} trading days`;
      return whole(spec.count, 1, MAX_SESSION_WEEKS)
        ? null
        : `weekly bars: 1 to ${String(MAX_SESSION_WEEKS)} weeks`;
    case "tick":
      return whole(spec.count, 1, MAX_TICKS) ? null : `tick bars: 1 to ${String(MAX_TICKS)} trades`;
    case "volume":
      return whole(spec.count, 1, MAX_VOLUME) ? null : `volume bars: 1 to ${String(MAX_VOLUME)} contracts`;
    case "range":
      if (spec.phantom !== undefined && typeof spec.phantom !== "boolean")
        return "range bars: phantom is a boolean";
      return whole(spec.ticks, 1, MAX_RANGE_TICKS)
        ? null
        : `range bars: 1 to ${String(MAX_RANGE_TICKS)} ticks`;
    case "renko":
      return whole(spec.ticks, 1, MAX_RANGE_TICKS)
        ? null
        : `Renko bricks: 1 to ${String(MAX_RANGE_TICKS)} ticks`;
  }
}

function checked(spec: BarSpec): BarSpec | null {
  return specError(spec) === null ? spec : null;
}

function build(n: number, unit: string): BarSpec | null {
  if (!Number.isSafeInteger(n) || n <= 0) return null;
  switch (unit) {
    case "s":
      return n <= MAX_SECONDS ? checked({ kind: "time", periodMs: n * S }) : null;
    case "m":
      return n <= MAX_MINUTES ? checked({ kind: "time", periodMs: n * MIN }) : null;
    case "h":
      return n <= MAX_HOURS ? checked({ kind: "time", periodMs: n * HR }) : null;
    case "D":
      return checked({ kind: "session", unit: "day", count: n });
    case "W":
      return checked({ kind: "session", unit: "week", count: n });
    case "mo":
      return checked({ kind: "session", unit: "month", count: n });
    case "t":
      return checked({ kind: "tick", count: n });
    case "v":
      return checked({ kind: "volume", count: n });
    case "r":
      return checked({ kind: "range", ticks: n });
    case "rk":
      return checked({ kind: "renko", ticks: n });
    default:
      return null;
  }
}

/** Parse a canonical timeframe ("1m", "4h", "1D", "3mo", "500t", "8r", "1000v", "4rk"). */
export function parseTimeframe(tf: string): BarSpec | null {
  const m = /^(\d{1,7})(s|m|h|D|W|mo|t|v|r|rk)$/.exec(tf.trim());
  if (!m) return null;
  return build(Number(m[1]), m[2] ?? "");
}

/** The canonical string of a spec; `parseTimeframe(formatTimeframe(s))` returns `s`. */
export function formatTimeframe(spec: BarSpec): string {
  switch (spec.kind) {
    case "time": {
      const p = spec.periodMs;
      if (p % HR === 0) return `${String(p / HR)}h`;
      if (p % MIN === 0) return `${String(p / MIN)}m`;
      return `${String(p / S)}s`;
    }
    case "session":
      return `${String(spec.count)}${spec.unit === "day" ? "D" : spec.unit === "week" ? "W" : "mo"}`;
    case "tick":
      return `${String(spec.count)}t`;
    case "volume":
      return `${String(spec.count)}v`;
    case "range":
      return `${String(spec.ticks)}r`;
    case "renko":
      return `${String(spec.ticks)}rk`;
  }
}

/**
 * A stored timeframe (layout params, favourites) → canonical string, or null. Besides canonical
 * strings it accepts the old picker's display labels: "1M"/"5M"/"15M" meant minutes and "500T"
 * ticks (the old picker stored lowercase, but a hand-edited or imported layout may carry labels).
 */
export function migrateTimeframe(stored: string): string | null {
  const direct = parseTimeframe(stored);
  if (direct) return formatTimeframe(direct);
  const m = /^(\d{1,7})(M|T|d|w)$/.exec(stored.trim());
  if (!m) return null;
  const unit = { M: "m", T: "t", d: "D", w: "W" }[m[2] as "M" | "T" | "d" | "w"];
  const spec = build(Number(m[1]), unit);
  return spec ? formatTimeframe(spec) : null;
}

const INPUT_UNITS: Record<string, string> = {
  s: "s",
  sec: "s",
  secs: "s",
  second: "s",
  seconds: "s",
  m: "m",
  min: "m",
  mins: "m",
  minute: "m",
  minutes: "m",
  h: "h",
  hr: "h",
  hrs: "h",
  hour: "h",
  hours: "h",
  d: "D",
  day: "D",
  days: "D",
  w: "W",
  wk: "W",
  week: "W",
  weeks: "W",
  mo: "mo",
  mon: "mo",
  mth: "mo",
  month: "mo",
  months: "mo",
  t: "t",
  tick: "t",
  ticks: "t",
  v: "v",
  vol: "v",
  volume: "v",
  r: "r",
  range: "r",
  rk: "rk",
  renko: "rk",
};

/**
 * Lenient user input: "7m", "7 min", "377t", "8r", "1000v", "4rk", "4 renko", "1d", "1w", "3mo".
 * A bare number means minutes (TradingView). Units are case-insensitive except that an uppercase
 * `M` alone is a month ("1M", TradingView convention).
 */
export function parseTimeframeInput(text: string): BarSpec | null {
  const m = /^(\d{1,7})\s*([a-zA-Z]*)$/.exec(text.trim());
  if (!m) return null;
  const n = Number(m[1]);
  const raw = m[2] ?? "";
  if (raw === "") return build(n, "m");
  if (raw === "M") return build(n, "mo");
  const unit = INPUT_UNITS[raw.toLowerCase()];
  return unit === undefined ? null : build(n, unit);
}

const plural = (n: number, one: string, many = `${one}s`): string =>
  `${n.toLocaleString("en-US")} ${n === 1 ? one : many}`;

/** Long name for tooltips and the picker ("5 minutes", "8-tick range", "1,000 contracts"). */
export function timeframeTitle(spec: BarSpec): string {
  switch (spec.kind) {
    case "time": {
      const p = spec.periodMs;
      if (p % HR === 0) return plural(p / HR, "hour");
      if (p % MIN === 0) return plural(p / MIN, "minute");
      return plural(p / S, "second");
    }
    case "session":
      if (spec.unit === "day")
        return spec.count === 1 ? "1 trading day (17:00 CT open)" : plural(spec.count, "trading day");
      if (spec.unit === "week")
        return spec.count === 1 ? "1 week (Sunday open to Friday close)" : plural(spec.count, "week");
      return spec.count === 12 ? "12 months (year)" : plural(spec.count, "month");
    case "tick":
      return plural(spec.count, "trade") + " per bar";
    case "volume":
      return plural(spec.count, "contract") + " per bar";
    case "range":
      return `${plural(spec.ticks, "tick")} range`;
    case "renko":
      return `Renko, ${plural(spec.ticks, "tick")} brick`;
  }
}

export type TimeframeGroup =
  "Seconds" | "Minutes" | "Hours" | "Days" | "Ticks" | "Range" | "Volume" | "Renko";

export function timeframeGroup(spec: BarSpec): TimeframeGroup {
  switch (spec.kind) {
    case "time":
      return spec.periodMs < MIN
        ? "Seconds"
        : spec.periodMs < HR || spec.periodMs % HR !== 0
          ? "Minutes"
          : "Hours";
    case "session":
      return "Days";
    case "tick":
      return "Ticks";
    case "volume":
      return "Volume";
    case "range":
      return "Range";
    case "renko":
      return "Renko";
  }
}

/** The picker's preset lists (SPEC §12.2A defaults plus the common tick counts). */
export const TIMEFRAME_PRESETS: readonly {
  group: TimeframeGroup;
  label: string;
  items: readonly string[];
}[] = [
  { group: "Seconds", label: "Seconds", items: ["1s", "5s", "10s", "15s", "30s"] },
  { group: "Minutes", label: "Minutes", items: ["1m", "2m", "3m", "5m", "10m", "15m", "30m", "45m"] },
  { group: "Hours", label: "Hours", items: ["1h", "2h", "3h", "4h", "6h", "8h", "12h"] },
  {
    group: "Days",
    label: "Days · Weeks · Months",
    items: ["1D", "2D", "3D", "1W", "2W", "1mo", "3mo", "6mo", "12mo"],
  },
  {
    group: "Ticks",
    label: "Ticks",
    items: [
      "1t",
      "10t",
      "25t",
      "50t",
      "100t",
      "144t",
      "233t",
      "250t",
      "500t",
      "1000t",
      "1500t",
      "2000t",
      "5000t",
    ],
  },
  { group: "Range", label: "Range (ticks)", items: ["4r", "8r", "12r", "16r", "20r"] },
  { group: "Volume", label: "Volume (contracts)", items: ["500v", "1000v", "2500v", "5000v"] },
  { group: "Renko", label: "Renko (brick ticks)", items: ["4rk", "8rk", "12rk", "16rk"] },
];

/** Favourites shown in the chart header until the user pins their own. */
export const DEFAULT_TIMEFRAME_FAVORITES: readonly string[] = ["1m", "5m", "15m", "1h", "1D", "500t"];
export const MAX_TIMEFRAME_FAVORITES = 16;

/** Canonicalise, drop invalid and duplicate entries, cap the length (synced settings input). */
export function sanitizeFavorites(list: unknown): string[] {
  if (!Array.isArray(list)) return [...DEFAULT_TIMEFRAME_FAVORITES];
  const out: string[] = [];
  for (const v of list) {
    const tf = typeof v === "string" ? migrateTimeframe(v) : null;
    if (tf !== null && !out.includes(tf)) out.push(tf);
    if (out.length >= MAX_TIMEFRAME_FAVORITES) break;
  }
  return out;
}

/** Pin or unpin `tf`; favourites stay in the order of `TIMEFRAME_PRESETS`-like duration order. */
export function toggleFavorite(list: readonly string[], tf: string): string[] {
  const canon = migrateTimeframe(tf);
  const cur = sanitizeFavorites(list);
  if (canon === null) return cur;
  if (cur.includes(canon)) return cur.filter((x) => x !== canon);
  return sortTimeframes([...cur, canon]).slice(0, MAX_TIMEFRAME_FAVORITES);
}

const GROUP_ORDER: readonly TimeframeGroup[] = [
  "Seconds",
  "Minutes",
  "Hours",
  "Days",
  "Ticks",
  "Range",
  "Volume",
  "Renko",
];

function sizeOf(spec: BarSpec): number {
  switch (spec.kind) {
    case "time":
      return spec.periodMs;
    case "session":
      // Calendar days: a trading day ≈ 7/5, so 5D sorts with 1W.
      return spec.unit === "day"
        ? (7 * spec.count) / 5
        : spec.unit === "week"
          ? 7 * spec.count
          : 31 * spec.count;
    case "tick":
    case "volume":
      return spec.count;
    case "range":
    case "renko":
      return spec.ticks;
  }
}

/** Group order (time → trade-built), then size within a group. Invalid strings go last. */
export function sortTimeframes(list: readonly string[]): string[] {
  const key = (tf: string): [number, number] => {
    const s = parseTimeframe(tf);
    return s ? [GROUP_ORDER.indexOf(timeframeGroup(s)), sizeOf(s)] : [99, 0];
  };
  return [...list].sort((a, b) => {
    const ka = key(a);
    const kb = key(b);
    return ka[0] - kb[0] || ka[1] - kb[1];
  });
}

/**
 * A chart's range-bar phantom setting (Q-T5: on by default, a per-chart toggle persisted with the
 * panel, not part of the timeframe string) applied to its spec. Other bar types are unchanged, and
 * "on" is the spec without the field, so specs and cache keys of existing charts do not change.
 */
export function withRangePhantoms(spec: BarSpec, on: boolean): BarSpec {
  if (spec.kind !== "range") return spec;
  return on ? { kind: "range", ticks: spec.ticks } : { kind: "range", ticks: spec.ticks, phantom: false };
}

/** Trade-built bar types: history is replayed from raw trades, and bars reset each session. */
export function isTradeBuilt(spec: BarSpec): boolean {
  return spec.kind === "tick" || spec.kind === "volume" || spec.kind === "range" || spec.kind === "renko";
}
